/**
 * WelcomeHouseholdModal Component
 *
 * Shown when a user joins their second household for the first time.
 * Explains multi-household functionality and offers to set the
 * joined household as the default.
 *
 * WP-HOUSEHOLD-2: Household Invitation Onboarding
 *
 * Accessibility:
 * - Focus trap within modal
 * - Keyboard navigation support
 * - Proper ARIA attributes
 * - Screen reader announcements
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { useSetDefaultHousehold, useMyHouseholds } from '@/hooks/useHouseholds';
import { toast } from '@/components/ui/Toaster';

export interface WelcomeHouseholdModalProps {
  isOpen: boolean;
  onClose: () => void;
  joinedHouseholdName: string;
  joinedHouseholdId: string;
}

/**
 * Get initials from a household name
 */
function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  const firstWord = words[0];
  const secondWord = words[1];
  if (words.length >= 2 && firstWord && secondWord && firstWord[0] && secondWord[0]) {
    return (firstWord[0] + secondWord[0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export default function WelcomeHouseholdModal({
  isOpen,
  onClose,
  joinedHouseholdName,
  joinedHouseholdId,
}: WelcomeHouseholdModalProps) {
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const setDefaultHousehold = useSetDefaultHousehold();
  const { data: householdsData } = useMyHouseholds();

  // Focus the continue button when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => continueButtonRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Allow Escape to close, but still navigate to dashboard
        handleContinue();
      }
      // Trap focus within modal
      if (event.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleContinue = useCallback(async () => {
    setIsLoading(true);

    try {
      if (setAsDefault) {
        try {
          await setDefaultHousehold.mutateAsync(joinedHouseholdId);
          toast.success(`${joinedHouseholdName} is now your default household`);
        } catch {
          // Non-blocking error - log but continue to dashboard
          // User can set default later in settings
        }
      }

      onClose();
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [setAsDefault, joinedHouseholdId, joinedHouseholdName, setDefaultHousehold, onClose, navigate]);

  // Find the other household (personal one)
  const otherHousehold = householdsData?.households.find(
    (h) => h.id !== joinedHouseholdId
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative transform overflow-hidden rounded-lg bg-white text-center shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md"
        >
          <div className="px-6 py-8">
            {/* Celebration icon */}
            <div className="mx-auto w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>

            {/* Title */}
            <h2
              id="welcome-modal-title"
              className="text-xl font-semibold text-gray-900 mb-2"
            >
              Welcome to {joinedHouseholdName}!
            </h2>

            {/* Description */}
            <p className="text-gray-600 mb-6">
              You now have access to <strong>2 households</strong>:
            </p>

            {/* Household list */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="space-y-3">
                {/* Joined household */}
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold">
                    {getInitials(joinedHouseholdName)}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{joinedHouseholdName}</div>
                    <div className="text-xs text-primary-600">Viewing now</div>
                  </div>
                </div>

                {/* Personal household */}
                {otherHousehold && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold">
                      {getInitials(otherHousehold.name)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{otherHousehold.name}</div>
                      <div className="text-xs text-gray-500">Your personal household</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Switcher hint */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 text-left">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <div className="font-medium text-blue-900 mb-1">
                    Switching between households
                  </div>
                  <div className="text-sm text-blue-700">
                    Use the household switcher in the top navigation bar to switch
                    between your households at any time.
                  </div>
                </div>
              </div>
            </div>

            {/* Default household checkbox */}
            <label className="flex items-center gap-3 mb-6 cursor-pointer justify-center">
              <input
                type="checkbox"
                checked={setAsDefault}
                onChange={(e) => setSetAsDefault(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                aria-describedby="default-checkbox-description"
              />
              <span id="default-checkbox-description" className="text-sm text-gray-700">
                Make <strong>{joinedHouseholdName}</strong> my default household
              </span>
            </label>

            {/* Continue button */}
            <Button
              ref={continueButtonRef}
              variant="primary"
              onClick={handleContinue}
              isLoading={isLoading}
              className="w-full"
            >
              Continue to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
